import React, { useEffect } from 'react';
import { RootState } from 'redux/rootReducer';
import { useDispatch, useSelector } from 'react-redux';

import { loadCountries } from 'redux/reducers/countries';
import {
  loadCustomers,
  selectCustomersByFeedType,
  selectDownloadCustomers,
} from 'redux/reducers/customers';
import { loadExCountries } from 'redux/reducers/exCountries';
import capitalize from 'lodash.capitalize';
import { loadRegions } from 'redux/reducers/regions';
import { loadGenres } from 'redux/reducers/genres';
import Span from 'components/elements/span';
import colors from 'styles/themes/colors';
import { loadCustomRegions } from 'redux/reducers/customRegions';
import usePlatforms from '../../../hooks/usePlatforms';

const noDataMessage = '(We no longer receive data from this DSP)';
export const allPlatforms = [
  { id: 285184, label: 'TikTok', name: 'tiktok' },
  { id: 285185, label: 'Douyin', name: 'douyin' },
  { id: 289673, label: 'WeSee', name: 'wesee' },
  { id: 291943, label: 'YouTube Shorts', name: 'youtubeShorts' },
];
export const tiktokIds = [allPlatforms[0].id, allPlatforms[1].id];

interface IFilterDisplayName {
  /** This is the filter type. Eg. 'country' */
  filter: string;
  /** This is the specific filter value. Eg. 'NL' */
  filterId: any;
  fontWeight?: any;
}

/** Displays the displayName of a filter by using the customers data. */
const FilterDisplayName = ({
  filter,
  filterId,
  fontWeight,
}: IFilterDisplayName): any => {
  const dispatch = useDispatch();

  // Load required redux store data on mount
  useEffect(() => {
    dispatch(loadCountries());
    dispatch(loadExCountries());
    dispatch(loadCustomers());
    dispatch(loadRegions());
    dispatch(loadCustomRegions());
    dispatch(loadGenres());
  }, [dispatch]);

  const { selectedPlatform } = usePlatforms({ [filter]: filterId });

  // Get the display names for the current filter
  const customers = useSelector((state: RootState) => {
    if (filter === 'country')
      return [...state.countries.data, ...state.exCountries.data];
    if (filter === 'download') return selectDownloadCustomers(state);
    return selectCustomersByFeedType(filter)(state) || [];
  });

  const regions = useSelector((state: RootState) => state.regions.data);
  const customRegions = Object.values(
    useSelector((state: RootState) => state.customRegions.data)
  );
  const genres = useSelector((state: RootState) => state.genres.data);

  if (filter === 'media') {
    return capitalize(filterId as string);
  }

  if (filter === 'release') {
    return capitalize(filterId as string);
  }

  if (filter === 'platform') {
    return selectedPlatform?.label ?? '';
  }

  if (filter === 'genre') {
    if (filterId?.length) {
      const selectedGenres = genres.filter((genre: any) =>
        filterId.includes(genre.genreCode.toString())
      );
      const genreNames = selectedGenres.map((g) => g.genreName);
      return genreNames.join(', ') ?? '';
    }
    return (genre: any): boolean => genre.genreCode.toString() === filterId;
  }

  if (filter === 'region') {
    if (filterId?.length) {
      const selectedRegions = regions.filter((region: any) =>
        filterId.includes(region.id)
      );
      const regionNames = selectedRegions.map((r) => r.displayName);
      return regionNames.join(', ') ?? '';
    }
    return (region: any): boolean => region.id === filterId;
  }

  if (filter === 'customRegion') {
    if (filterId?.length) {
      const selectedRegions = customRegions.filter((region: any) =>
        filterId.includes(region.id)
      );
      const regionNames = selectedRegions.map((r) => r.displayName);
      return regionNames.join(', ') ?? '';
    }
    return (region: any): boolean => region.id === filterId;
  }

  if (filter === 'country' && filterId === 'WW') {
    return 'Global';
  }

  let appliedFilter;

  if (filter === 'country' && typeof filterId !== 'string') {
    const filteredCountries = customers.filter((customer: any) =>
      filterId.includes(customer.id || customer.name)
    );

    const displayName = filteredCountries
      .map(
        (country: any) => `${country.isEx ? 'EX ' : ''} ${country.displayName}`
      )
      .join(', ');

    appliedFilter = { displayName };
  } else {
    appliedFilter = customers.find((customer: any) => {
      return customer.id === filterId || customer.name === filterId;
    });
  }

  if (appliedFilter === undefined) return null;

  const isWarned =
    (filter === 'streaming' || filter === 'download') &&
    appliedFilter.isDeprecated;
  const filterName = isWarned
    ? `${appliedFilter.displayName} ${noDataMessage}`
    : appliedFilter.displayName;

  return (
    <Span color={isWarned ? colors.NegativeChange : ''} fontWeight={fontWeight}>
      {`${
        filter === 'country' && appliedFilter.isEx ? 'EX ' : ''
      }${filterName}`}
    </Span>
  );
};

export default FilterDisplayName;
